/* global FinvizQuoteTickers */
import classnames from 'classnames'
import * as dateFns from 'date-fns'
import React, { ChangeEvent, Component, FormEvent } from 'react'

import { DatePicker } from '../../../main/components/date-picker'
import { SpecificChartFunctionality } from '../../../main/constants'
import {
  ChartElementType,
  ChartLayout,
  Instrument,
  Quote,
  QuoteFetchType,
  TIMEFRAME,
  Theme,
  Utils,
} from '../../charts-lib'
import { decodeQueryString } from '../../queryString'
import { getStaticChartConfig } from '../../shared/chartLayoutConfig'
import { getSanitizedTicker } from '../../shared/ticker-sanitizer'
import { formatDateToStringUS } from '../../shared/utils'

const redesignEnabled = !!FinvizSettings.hasRedesignEnabled

var colorScaleClass = function (value: number) {
  if (value < 0) {
    return 'color-text is-negative'
  } else if (value > 0) {
    return 'color-text is-positive'
  }
  return ''
}

var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

var arraysEqual = function (arr1: unknown[], arr2: unknown[]) {
  if (arr1.length !== arr2.length) {
    return false
  }
  for (var i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false
    }
  }
  return true
}

interface ChartProps {
  tickers: string[]
  fromDay: string
  toDay: string
}

class Chart extends Component<ChartProps> {
  containerRef = React.createRef<HTMLDivElement>()

  shouldComponentUpdate(nextProps: ChartProps) {
    return !arraysEqual(this.props.tickers, nextProps.tickers)
  }

  render() {
    const theme = FinvizSettings.hasDarkTheme ? Theme.dark : Theme.light
    const tickerQuote = Quote.select<Quote>((q) => q.close.length > 0 && this.props.tickers.includes(q.ticker))
    const chartConfig = getStaticChartConfig({
      ticker: tickerQuote[0]?.ticker ?? '',
      timeframe: TIMEFRAME.d,
      paneElements: [
        {
          type: ChartElementType.PerfChart,
          tickers: this.props.tickers,
          fromDate: this.props.fromDay,
          toDate: this.props.toDay,
          overlays: [],
        },
      ],
      width: 1000,
      height: 450,
      refreshData: false,
      specificChartFunctionality: SpecificChartFunctionality.quotePerf,
      theme,
    })
    return (
      <div ref={this.containerRef} id="chart" className="h-[452px] w-[1000px]" style={{ opacity: 0 }}>
        <ChartLayout config={chartConfig} parentElement={this.containerRef} />
      </div>
    )
  }
}

interface TableProps {
  months: string[]
  rows: Array<{
    ticker: string
    months: string[]
    total: string
  }>
}

// mozno vyfarbovat tabulku?
class Table extends Component<TableProps> {
  render() {
    var { months, rows } = this.props,
      groupsCount = Math.ceil(months.length / 12)
    rows = rows.slice()
    const tdClasses = classnames('text-center', {
      'body-table-nw font-bold': !redesignEnabled,
    })

    var trs = []
    for (let g = 0; g < groupsCount; g++) {
      trs.push(
        <tr
          key={g}
          className={classnames({
            'table-dark-row': !redesignEnabled,
            'styled-row is-bordered is-rounded is-highlighted': redesignEnabled,
          })}
        >
          <td className={tdClasses}></td>
          {months.splice(0, 12).map((month) => (
            <td key={month} className={tdClasses}>
              {month}
            </td>
          ))}
          {g === groupsCount - 1 && <td className={tdClasses}>Total</td>}
        </tr>
      )
      rows.forEach((row, i) => {
        trs.push(
          <tr
            key={`${g}-${i}`}
            className={classnames({
              'styled-row is-bordered is-rounded': redesignEnabled,
              'table-light-row': !redesignEnabled && i % 2 === 0,
              'table-dark-row': !redesignEnabled && i % 2 !== 0,
              'is-highlighted': redesignEnabled && i % 2 !== 0,
            })}
          >
            <td className={classnames('text-left', { 'body-table-nw': !redesignEnabled })}>{row.ticker}</td>
            {row.months.splice(0, 12).map((col, monthIndex) => (
              <td
                key={`${row.ticker}-${col}-${monthIndex}`}
                className={classnames('text-right', colorScaleClass(parseFloat(col)), {
                  'body-table-nw': !redesignEnabled,
                })}
              >
                {col}
              </td>
            ))}
            {g === groupsCount - 1 && (
              <td
                className={classnames('text-right', colorScaleClass(parseFloat(row.total)), {
                  'body-table-nw': !redesignEnabled,
                })}
              >
                {row.total}
              </td>
            )}
          </tr>
        )
      })
    }

    return (
      <table
        className={classnames({
          'styled-table-new is-rounded quote_perf-table': redesignEnabled,
          'table-perf': !redesignEnabled,
        })}
        cellPadding="3"
        cellSpacing="1"
        // @ts-ignore
        border="0"
      >
        <tbody>{trs}</tbody>
      </table>
    )
  }
}

interface PerfState {
  tickersInput: string
  tickers: string[]
  months: Array<{
    value: string
    label: string
  }>
  fromMonth: {
    value: string
    label: string
  }
  toMonth: {
    value: string
    label: string
  }
  minDate: string
  maxDate: string
  fromDay: string
  toDay: string
  loading: boolean
}

export class Perf extends Component<any, PerfState> {
  constructor(props: any) {
    super(props)
    var now = new Date(),
      months = []
    const startYear = new Date().getFullYear() - 36 // We have 37.25 years of data in DB, so leaving some offset
    for (var year = startYear; year <= now.getFullYear(); year++) {
      for (var month = 1; month <= 12; month++) {
        if (year === now.getFullYear() && month > now.getMonth() + 1) {
          break
        }
        months.push({
          value: month.toString().padStart(2, '0') + '/01/' + year,
          label: monthNames[month - 1] + ' ' + year.toString().substring(2),
        })
      }
    }

    this.state = {
      tickersInput: FinvizQuoteTickers.join(','),
      tickers: FinvizQuoteTickers,
      months: months,
      fromMonth: months[months.length - 12],
      toMonth: months[months.length - 1],
      /**
       * DB_BARS_LIMIT - code below is dependent on amount of data we store for specific timeframes
       * Search DB_BARS_LIMIT in the project to find other places with such code.
       */
      // 2510 bars = ±10 years
      minDate: formatDateToStringUS(dateFns.sub(new Date(), { years: 9, months: 11 })),
      maxDate: formatDateToStringUS(new Date()),
      fromDay: formatDateToStringUS(dateFns.subYears(new Date(), 1)),
      toDay: formatDateToStringUS(new Date()),
      loading: true,
    }
  }

  render() {
    if (this.state.loading) {
      return (
        <div
          className="mx-auto mb-4 flex items-center justify-center"
          style={{ minHeight: FinvizSettings.hasRedesignEnabled ? 640 : 635 }}
        >
          Loading...
        </div>
      )
    }

    var from = new Date(this.state.fromMonth.value)
    var to = new Date(this.state.toMonth.value)
    var monthsToShow = this.state.months
      .map((month) => month.value)
      .filter((month) => new Date(month) >= from && new Date(month) <= to)

    var quotes = Quote.select((q: any) => q.timeframe === 'm')
    var quotesPerf = quotes.map((q: any) => {
      var obj: { ticker: string; perf: Record<string, number> } = { ticker: q.ticker, perf: {} }
      q.date.forEach((d: any, i: number) => {
        if (i === 0) {
          return
        }
        var date = Utils.dateFromUnixTimestamp(d)
        var perf = (q.close[i] * 100) / q.close[i - 1] - 100
        obj.perf[(date.getMonth() + 1).toString().padStart(2, '0') + '/01/' + date.getFullYear()] = perf
      })
      return obj
    })

    var months = monthsToShow.map(
      (month) => monthNames[new Date(month).getMonth()] + ' ' + new Date(month).getFullYear().toString().substring(2)
    )
    var rows = quotesPerf.map((quote: any) => {
      var cols: string[] = []
      var total = 1
      monthsToShow.forEach((month) => {
        var perf = Math.round(quote.perf[month] * 100) / 100
        if (!isNaN(perf)) {
          total *= quote.perf[month] / 100 + 1
          cols.push((perf > 0 ? '+' : '') + perf.toFixed(2) + '%')
        } else {
          cols.push('N/A')
        }
      })
      total = (total - 1) * 100
      return { ticker: quote.ticker, months: cols, total: (total > 0 ? '+' : '') + total.toFixed(2) + '%' }
    })

    return (
      <div className="perf-page relative mx-auto mb-4 flex w-[1005px] flex-col items-center">
        <form onSubmit={this._onSubmit} className="mb-2.5 w-full">
          <table className="quote_perf-tickers-input">
            <tbody>
              <tr>
                <td>Tickers:</td>
                <td width="100%" style={{ padding: '0 5px' }}>
                  <input
                    className="fv-input"
                    type="text"
                    value={this.state.tickersInput}
                    onChange={(e) => {
                      this.setState({ tickersInput: e.target.value })
                    }}
                    style={{ width: '100%' }}
                    data-testid="quote-compare-perf-tickers-input"
                  />
                </td>
                <td>
                  <input
                    className="fv-button is-small"
                    type="submit"
                    value="Submit"
                    data-testid="quote-compare-perf-tickers-input-submit-btn"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </form>

        <Chart tickers={this.state.tickers} fromDay={this.state.fromDay} toDay={this.state.toDay} />

        <form
          className="quote-range"
          style={{ position: 'absolute', top: 39, right: redesignEnabled ? 5 : 3, fontSize: 13 }}
          data-testid="quote-compare-perf-quote-range-form"
        >
          <div style={{ position: 'relative', width: 80, display: 'inline-block' }}>
            <DatePicker
              minDate={new Date(this.state.minDate)}
              maxDate={new Date(this.state.maxDate)}
              value={new Date(this.state.fromDay)}
              onChange={(newValue: Date) => {
                this._onDatePickerChange('from', formatDateToStringUS(newValue))
              }}
            >
              <input type="input" readOnly className="fv-input w-full" value={this.state.fromDay} />
            </DatePicker>
          </div>
          {' - '}
          <div style={{ position: 'relative', width: 80, display: 'inline-block' }}>
            <DatePicker
              minDate={new Date(this.state.minDate)}
              maxDate={new Date(this.state.maxDate)}
              value={new Date(this.state.toDay)}
              onChange={(newValue: Date) => {
                this._onDatePickerChange('to', formatDateToStringUS(newValue))
              }}
            >
              <input type="input" readOnly className="fv-input w-full" value={this.state.toDay} />
            </DatePicker>
          </div>
        </form>

        <h2 className="quote_perf-table-heading" data-testid="quote-compare-perf-monthly-performance-container">
          Monthly performance from&nbsp;&nbsp;
          <select
            className="fv-select"
            value={this.state.fromMonth.value}
            onChange={this._onMonthChange.bind(this, 'from')}
            style={{ fontWeight: 'normal' }}
          >
            {this.state.months.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          &nbsp;&nbsp;to&nbsp;&nbsp;
          <select
            className="fv-select"
            value={this.state.toMonth.value}
            onChange={this._onMonthChange.bind(this, 'to')}
            style={{ fontWeight: 'normal' }}
          >
            {this.state.months.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </h2>
        <Table months={months} rows={rows} />

        <br />
      </div>
    )
  }

  refetchQuotes = () => {
    this._fetch(this.state.tickers, false)
  }

  componentDidMount() {
    this._fetch(this.state.tickers)

    // We have to listen to these events to synchrnously update quote data, if we do after awaiting Quote.getAll in _fetch
    // there's a visual glitch because it rerenders with new data from the server (there's a complete refetch after 15 requests with no new data)
    // and then it rerenders again after updateQuoteData
    Quote.bind('refetch newerData', this.updateQuoteData)
    document.addEventListener('quoteFetchEvent', this.refetchQuotes)
  }

  componentWillUnmount() {
    Quote.unbind('refetch newerData', this.updateQuoteData)
    document.removeEventListener('quoteFetchEvent', this.refetchQuotes)
  }

  updateQuoteData = () => {
    const { fromDay, toDay } = this.state

    if (!fromDay || !toDay) {
      return
    }

    const dailyQuotes = Quote.select((q: any) => q.timeframe === 'd')
    const fromDate = new Date(fromDay + ' 00:00')
    const toDate = new Date(toDay + ' 23:59')
    dailyQuotes.forEach((q: any) => {
      const o = [],
        h = [],
        l = [],
        c = [],
        d = [],
        v = []
      for (let i = 0; i < q.close.length; i++) {
        const date = Utils.dateFromUnixTimestamp(q.date[i])
        if (fromDate <= date && date <= toDate) {
          o.push(q.open[i])
          h.push(q.high[i])
          l.push(q.low[i])
          c.push(q.close[i])
          d.push(q.date[i])
          v.push(q.volume[i])
        }
      }
      // Clear cache so we'll recalculate values after updating quote data on next line
      // Cache invalidates only on quote.fetchedAt change and in this case we mutate quote values and don't change fetchedAt (which is correct)
      // Updating quote this way should be an edge case and that's why we have to clear the cache manually
      q.clearCachedData()
      q.updateAttributes({
        open: o,
        high: h,
        low: l,
        close: c,
        date: d,
        volume: v,
      })
    })

    // This is to rerender perf table on quote update
    this.forceUpdate()
  }

  _fetch = async (tickers: string[], isRefetch = true) => {
    const { t } = decodeQueryString<{ t: string; p?: TIMEFRAME }>()
    if (isRefetch) {
      this.setState({ loading: true })
    }

    Quote.all().forEach(async (quote) => {
      if (quote.ticker === t && quote.timeframe === 'd' && !isRefetch) {
        // We don't want to trigger main ticker quote newer data fetch because it's done in withQuotePolling which also triggers this function
        // by emmiting quoteFetchEvent
        return
      }
      if (tickers.includes(quote.ticker)) {
        await quote.fetchData({ fetchType: isRefetch ? QuoteFetchType.Refetch : QuoteFetchType.NewerData })
        this.updateQuoteData()
      } else {
        quote.destroy()
      }
    })

    if (isRefetch) {
      var d = tickers.map((ticker) => ({ ticker: ticker, timeframe: TIMEFRAME.d, instrument: Instrument.Stock }))
      var m = tickers.map((ticker) => ({ ticker: ticker, timeframe: TIMEFRAME.m, instrument: Instrument.Stock }))
      await Quote.getAll(d.concat(m))

      this.setState({ loading: false })
    }

    this.updateQuoteData()
  }

  _onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const tickersInput = getSanitizedTicker(this.state.tickersInput, true)
    const tickers = tickersInput.split(',')
    this.setState({ tickers, tickersInput }, () => {
      this._fetch(tickers)
    })
  }

  _onMonthChange = (type: string, e: ChangeEvent<HTMLSelectElement>) => {
    var state: any = {}
    state[type + 'Month'] = this.state.months.filter((m) => m.value === e.target.value)[0]
    this.setState(state)
  }

  _onDatePickerChange = (type: string, date: any) => {
    var formattedDate = formatDateToStringUS(new Date(date))

    var state: any = {}
    state[type + 'Day'] = formattedDate
    state[type + 'DatePickerOpen'] = false
    this.setState(state, () => {
      this._fetch(this.state.tickers)
    })
  }
}
